import { useState, type ReactNode } from 'react';
import { Badge, Card } from '../../../shared/ui';
import { Icon, type IconName } from '../../components/Icons';
import type { TenantTabProps } from '../TenantDetail';

type ItemState = 'done' | 'todo' | 'check' | 'manual';

interface Item {
  id: string;
  state: ItemState;
  title: string;
  how: ReactNode;
}

const STATE_ICON: Record<ItemState, IconName> = { done: 'check', todo: 'cross', check: 'question', manual: 'hand' };
const STATE_LABEL: Record<ItemState, string> = { done: 'Hecho', todo: 'Pendiente', check: 'Por comprobar', manual: 'Paso manual' };

const CADDYFILE = '/opt/calculadora-api/caddy-config/Caddyfile';
const RELOAD = 'docker exec caddy caddy reload --config /etc/caddy/Caddyfile';

/** Puesta en marcha: lo que tiene que estar hecho para que un cliente funcione. */
export function Checklist({ detail, goTo }: TenantTabProps): JSX.Element {
  const { tenant, overview } = detail;
  const host = new URL(tenant.portalUrl).host;
  const companies = tenant.companies.filter((c) => c.active);
  // Si SAP ha dado el CIF o un repaso salió bien, es que la conexión funcionó alguna vez.
  const connected = overview.lastSync?.ok === true || tenant.companies.some((c) => c.cifSource === 'sap');
  const link = (label: string, tab: Parameters<typeof goTo>[0]): JSX.Element => (
    <button type="button" className="link-btn" onClick={() => goTo(tab)}>
      {label}
    </button>
  );

  const items: Item[] = [
    {
      id: 'company',
      state: companies.length > 0 ? 'done' : 'todo',
      title: 'Sociedad de SAP dada de alta',
      how: <>Al menos una company_db con su usuario y contraseña del Service Layer. {link('Ir a Conexión SAP', 'conexion')}</>,
    },
    {
      id: 'connection',
      state: connected ? 'done' : 'check',
      title: 'Conexión con SAP probada',
      how: (
        <>
          {connected ? 'Hay un repaso correcto o un CIF leído de SAP. ' : 'Desde aquí no se puede saber: pulsa «Probar conexión» en cada sociedad. '}
          {link('Probar la conexión', 'conexion')}
        </>
      ),
    },
    {
      id: 'udfs',
      state: 'check',
      title: 'Campos de usuario U_EDI_ creados en SAP',
      how: <>Comprueba los campos y crea los que falten, con los usuarios fuera de SAP. {link('Ir a los campos', 'conexion')}</>,
    },
    {
      id: 'smtp',
      state: overview.smtpConfigured ? 'done' : 'todo',
      title: 'Servidor de correo (SMTP) configurado',
      how: <>Servidor y remitente para los avisos a proveedores y compras. Manda un correo de prueba. {link('Ir a Configuración', 'configuracion')}</>,
    },
    {
      id: 'drawings',
      state: overview.drawingsConfigured ? 'done' : 'todo',
      title: 'Repositorio de planos configurado',
      how: <>Carpeta montada con los planos replicados y el patrón de nombres. {link('Ir a Configuración', 'configuracion')}</>,
    },
    {
      id: 'admin',
      state: (overview.users.cliente_admin?.total ?? 0) > 0 ? 'done' : 'todo',
      title: 'Administrador del cliente creado',
      how: <>Un usuario con perfil «Administrador del cliente» que gestione su portal. {link('Ir a Usuarios', 'usuarios')}</>,
    },
    {
      id: 'suppliers',
      state: overview.suppliers.total > 0 ? 'done' : 'todo',
      title: 'Proveedores dados de alta',
      how: <>Lo hace el propio cliente desde su portal: habilita a sus proveedores y les crea usuario.</>,
    },
    {
      id: 'caddy',
      state: 'manual',
      title: 'Subdominio publicado en Caddy',
      how: <CaddySteps host={host} />,
    },
  ];

  const pending = items.filter((i) => i.state === 'todo').length;

  return (
    <Card
      title="Puesta en marcha"
      actions={
        <Badge tone={pending === 0 ? 'ok' : 'warn'}>{pending === 0 ? 'Nada pendiente' : `${pending} pendiente${pending === 1 ? '' : 's'}`}</Badge>
      }
    >
      <ol className="checklist">
        {items.map((item) => (
          <li key={item.id} className={`checklist-item ${item.state}`}>
            <span className={`check-icon ${item.state}`} title={STATE_LABEL[item.state]}>
              <Icon name={STATE_ICON[item.state]} size={14} />
              <span className="sr-only">{STATE_LABEL[item.state]}: </span>
            </span>
            <div className="grow">
              <div className="checklist-title">{item.title}</div>
              <div className="checklist-how">{item.how}</div>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function CaddySteps({ host }: { host: string }): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string): void => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(text);
      window.setTimeout(() => setCopied(null), 2000);
    });
  };
  return (
    <div className="stack sm caddy-steps">
      <span>
        En el servidor, añade este nombre a la lista de direcciones del bloque de Provalia en <code>{CADDYFILE}</code> (separado por
        comas, junto a los demás clientes):
      </span>
      <div className="code-line">
        <code>{host}</code>
        <button type="button" className="btn ghost sm" onClick={() => copy(host)}>
          {copied === host ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <span>y recarga Caddy:</span>
      <div className="code-line">
        <code>{RELOAD}</code>
        <button type="button" className="btn ghost sm" onClick={() => copy(RELOAD)}>
          {copied === RELOAD ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <span className="subtle">Caddy pide el certificado HTTPS solo. Comprueba que el portal abre en https://{host}.</span>
    </div>
  );
}
